// Lifting calorie burn — a RANGE, and deliberately nothing more.
//
// Resistance-training MET values have a wider spread than the signal: the
// compendium puts free-weight work anywhere from ~3.5 (light) to 6.0
// (vigorous), and nothing a workout row records can say where a session fell
// in that band. This number is decorative, not a datum — it renders like the
// swim card's "2,150–2,450 burned" range and drives NOTHING: no bar, no
// deficit, no target, no total anywhere else in the app.
//
// Pure functions only, same contract as src/food/burn.js: callers hand in
// rows, nothing here fetches anything. Absence is not zero — no weigh-in or
// no duration yields null, and the caller renders a dash or nothing at all.

const LIFT_MET_LOW = 4.0;
const LIFT_MET_HIGH = 6.0;

const LBS_PER_KG = 2.2046;

// Every burn figure rounds to the same step so a ~35-minute session's range
// still reads as a range. The swim card rounds to 50 on numbers an order of
// magnitude larger; 10 keeps the same "this is an estimate" statement at
// this scale.
export const LIFT_BURN_ROUNDING = 10;

// The most recent weigh-in with a usable number, by WeighDate. Rows whose
// WeightLbs is absent or non-numeric are skipped rather than read as zero.
export function latestWeightLbs(weighIns) {
  let best = null;
  let bestTime = -Infinity;
  for (const row of weighIns) {
    const lbs = row.WeightLbs;
    if (typeof lbs !== 'number' || !Number.isFinite(lbs)) continue;
    const t = new Date(row.WeighDate).getTime();
    if (!Number.isFinite(t) || t <= bestTime) continue;
    best = lbs;
    bestTime = t;
  }
  return best;
}

// MET × kg × hours at both ends of the band, or null when either input is
// missing — a burn computed from a guessed weight would be indistinguishable
// on screen from one computed from a measurement.
export function liftBurnRange(weightLbs, durationSeconds) {
  if (typeof weightLbs !== 'number' || !Number.isFinite(weightLbs) || weightLbs <= 0) return null;
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;
  const kg = weightLbs / LBS_PER_KG;
  const hours = durationSeconds / 3600;
  const round = cal => Math.round(cal / LIFT_BURN_ROUNDING) * LIFT_BURN_ROUNDING;
  return {
    lowCal: round(LIFT_MET_LOW * kg * hours),
    highCal: round(LIFT_MET_HIGH * kg * hours),
  };
}

// "150–230 burned", the swim card's range idiom. Null in, null out.
export function formatBurnRange(range) {
  if (range === null) return null;
  return `${range.lowCal.toLocaleString('en-US')}–${range.highCal.toLocaleString('en-US')} burned`;
}

// ── Template Sets parsing ────────────────────────────────────────────────────
// The ONE parser for a LiftingTemplates Sets column, shared by the runner,
// the templates tab and the dashboard — two implementations of the same
// parse could silently disagree about what a template contains.
//
// Current shape: a bare array of exercise entries; an entry may carry
// `alt` (per-exercise dumbbell fallback) and `muscleGroups`. The retired
// { variants: {...} } shape is tolerated by flattening to its first
// variant so an unmigrated row degrades to a working list, not an empty one.
export function parseTemplateSets(setsJson) {
  let parsed;
  try { parsed = JSON.parse(setsJson); } catch { parsed = null; }
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && parsed.variants && typeof parsed.variants === 'object') {
    const names = Object.keys(parsed.variants).filter(k => Array.isArray(parsed.variants[k]));
    if (names.length > 0) {
      // Loud on purpose. This flatten once served a 6-exercise null-weight
      // prescription that read on screen as two separate bugs; a silent
      // fallback is indistinguishable from current data.
      console.warn(`parseTemplateSets: legacy {variants} shape — flattened to "${names[0]}". Re-save this template to migrate it.`);
      return parsed.variants[names[0]];
    }
  }
  return [];
}

// ── Weekly hard sets by muscle group ─────────────────────────────────────────
//
// The retention metric: hard sets per muscle per week — not volume, not
// time. A hard set is one taken to RIR ≤ 2 (two-or-three reps in reserve or
// closer to failure). Targets muscle retention in a deficit at 2x/week
// full-body; hardcoded like PROTEIN_TARGET_G_PER_DAY, deliberately not
// editable and not a settings record.
export const HARD_SETS_TARGET_PER_MUSCLE_PER_WEEK = 6;

// RIR at or below this counts as a hard set. The capture stores 0 (failure),
// 1, 2 (two-or-three left) or 4 (easy); only 4 reads as not-hard.
export const HARD_RIR_MAX = 2;

// Accumulate hard-set counts per muscle group across saved workout entries.
//
// THE NULL RULE, inverted on purpose from the food module's: an unrated set
// (rir null — the tap was skipped, or the row predates capture) COUNTS as
// hard. Skipped taps must not read as zero work — overcounting a rest-day
// muscle is a smaller lie than erasing a set that happened. The unrated
// count rides along so the caller can mark the number as carrying that
// assumption, same caveat vocabulary as everywhere else.
//
// Entries without muscleGroups cannot be attributed and contribute nothing —
// the set happened, this metric just cannot see where.
//
// Returns { [muscle]: { hard, unrated } }.
export function hardSetsByMuscle(workoutsSets) {
  const byMuscle = {};
  for (const sets of workoutsSets) {
    if (!Array.isArray(sets)) continue;
    for (const entry of sets) {
      if (entry === null || typeof entry !== 'object') continue;
      const groups = Array.isArray(entry.muscleGroups)
        ? entry.muscleGroups.filter(g => typeof g === 'string' && g.trim() !== '')
        : [];
      if (groups.length === 0) continue;
      const setCount = Number(entry.actualSets ?? entry.sets);
      if (!Number.isFinite(setCount) || setCount <= 0) continue;
      const rirs = Array.isArray(entry.rirs) ? entry.rirs : [];
      for (let i = 0; i < setCount; i++) {
        const rir = Number.isFinite(Number(rirs[i])) && rirs[i] !== null ? Number(rirs[i]) : null;
        const unrated = rir === null;
        const hard = unrated || rir <= HARD_RIR_MAX;
        if (!hard) continue;
        for (const g of groups) {
          if (!byMuscle[g]) byMuscle[g] = { hard: 0, unrated: 0 };
          byMuscle[g].hard += 1;
          if (unrated) byMuscle[g].unrated += 1;
        }
      }
    }
  }
  return byMuscle;
}

// TotalVolumeLb — the money metric, lifting's TotalDistanceMeters.
// Σ weightLb × actualReps × actualSets over a workout's set entries.
// An entry missing any factor contributes nothing rather than a guess; a
// bodyweight movement with no load recorded is real work this metric simply
// cannot see, same as an untimed swim contributes no moving seconds.
export function totalVolumeLb(sets) {
  if (!Array.isArray(sets)) return 0;
  let volume = 0;
  for (const s of sets) {
    if (s === null || typeof s !== 'object') continue;
    const weight = Number(s.weightLb);
    const reps = Number(s.actualReps ?? s.reps);
    const setCount = Number(s.actualSets ?? s.sets);
    if (!Number.isFinite(weight) || weight <= 0) continue;
    if (!Number.isFinite(reps) || reps <= 0) continue;
    if (!Number.isFinite(setCount) || setCount <= 0) continue;
    volume += weight * reps * setCount;
  }
  return volume;
}
